import { administrationEnd, administrationEndAt, todayIn } from '@/common/medicationSchedule';

/**
 * Two live entries on one patient for the same drug, worked out on every load and never stored.
 *
 * One matcher for the med list, the eMAR and the Save-time "Already being given" question, so the
 * three can never disagree about what counts as the same drug.
 */

const RXNORM_SYSTEM = 'http://www.nlm.nih.gov/research/umls/rxnorm';

export const ENDED_STATUSES = ['stopped', 'cancelled', 'completed', 'entered-in-error'];

const rxNormCodesOf = (med) => (med?.medication?.CodeableConcept?.coding || []).filter((coding) => coding?.system === RXNORM_SYSTEM && coding?.code).map((coding) => String(coding.code));

const drugNameOf = (med) => (med?.medication?.CodeableConcept?.text || '').trim().toLowerCase();

/**
 * What a review was confirmed against: the RxNorm codes when there are any, else the name.
 *
 * @param {object} med
 * @returns {string} 'rx:<codes>', 'name:<name>' or ''
 */
export const drugKeyOf = (med) => {
    const codes = rxNormCodesOf(med);
    if (codes.length) return `rx:${[...codes].sort().join(',')}`;
    const name = drugNameOf(med);
    return name ? `name:${name}` : '';
};

/**
 * Whether two entries are the same drug: RxNorm overlap where both carry a code, otherwise the
 * same name, ignoring case and surrounding spaces. A medication with no code still schedules end
 * to end and is exactly the kind that gets typed in twice.
 *
 * @param {object} a
 * @param {object} b
 * @returns {boolean}
 */
export const sameDrug = (a, b) => {
    const codes = rxNormCodesOf(a);
    const otherCodes = rxNormCodesOf(b);
    if (codes.length && otherCodes.length) return otherCodes.some((code) => codes.includes(code));
    const name = drugNameOf(a);
    return Boolean(name) && drugNameOf(b) === name;
};

/**
 * Whether the order is still in force: not stopped, cancelled, completed or entered in error, and
 * not past its end day in the facility's zone. Draft, active and on-hold are all live.
 *
 * @param {object} med
 * @param {{today?: string, timeZone?: string}} [options]
 * @returns {boolean}
 */
export const isLiveOrder = (med, { today, timeZone } = {}) => {
    if (!med || ENDED_STATUSES.includes(med.status)) return false;
    const endDay = administrationEndAt(med, timeZone)?.slice(0, 10) || administrationEnd(med);
    return !(endDay && endDay < (today || todayIn(timeZone)));
};

const compareMoments = (a, b) => {
    const aTime = Date.parse(a);
    const bTime = Date.parse(b);
    if (Number.isFinite(aTime) && Number.isFinite(bTime) && aTime !== bTime) return aTime - bTime;
    return String(a).localeCompare(String(b));
};

// Added first sorts first: record creation, then the order's own date, then the id, so two
// entries always have an order even when neither carries a timestamp.
const compareAdded = (a, b) => {
    const aCreated = a?.meta?.createdAt;
    const bCreated = b?.meta?.createdAt;
    if (aCreated && bCreated && aCreated !== bCreated) return compareMoments(aCreated, bCreated);
    if (a?.authoredOn && b?.authoredOn && a.authoredOn !== b.authoredOn) return compareMoments(a.authoredOn, b.authoredOn);
    return String(a?.id ?? '').localeCompare(String(b?.id ?? ''));
};

// The reviews on `med` that still count for `other`: they name it, and the drug they were
// confirmed against is still this entry's drug. A changed drug lapses the review.
// A review names the other entry as `{ id, resourceType }`, the shape Aidbox stores a reference
// in; a literal `reference` is accepted too, so either form reads back as the same review.
const namesEntry = (ref, id) => Boolean(id) && (ref?.id === id || ref?.reference === `MedicationRequest/${id}`);

const reviewsFor = (med, other) => {
    const drugKey = drugKeyOf(med);
    return (med?.duplicateReview || []).filter((review) => namesEntry(review?.medication, other.id) && review?.drugKey === drugKey);
};

const latestOf = (reviews) => {
    const latest = [...reviews].sort((a, b) => compareMoments(a?.date || '', b?.date || '')).at(-1);
    return latest ? { practitioner: latest.practitioner, date: latest.date } : null;
};

/**
 * Every live entry that shares its drug with another live entry, keyed by id.
 *
 * The earliest added is 'earlier'; every other is 'later'. Each lists all the other entries it
 * matches. An inactive entry never appears and never makes a partner appear.
 *
 * A 'later' entry is reviewed once it carries a still-valid review for every other entry. An
 * 'earlier' entry is reviewed once every 'later' partner carries one for it.
 *
 * @param {Array<object>} medications
 * @param {{today?: string, timeZone?: string}} [options]
 * @returns {Map<string, {role: 'later'|'earlier', others: Array<object>, reviewedBy: {practitioner: object, date: string}|null}>}
 */
export const duplicatesOf = (medications, { today, timeZone } = {}) => {
    const day = today || todayIn(timeZone);
    const live = (medications || []).filter((med) => med?.id && isLiveOrder(med, { today: day, timeZone }));
    const result = new Map();

    for (const med of live) {
        const others = live.filter((other) => other.id !== med.id && sameDrug(med, other)).sort(compareAdded);
        if (!others.length) continue;

        // Every match of the earliest entry was added after it, so each of them is 'later'.
        const earlier = compareAdded(med, others[0]) < 0;
        const reviews = others.map((other) => (earlier ? reviewsFor(other, med) : reviewsFor(med, other)));
        const reviewedBy = reviews.every((found) => found.length) ? latestOf(reviews.flat()) : null;

        result.set(med.id, { role: earlier ? 'earlier' : 'later', others, reviewedBy });
    }

    return result;
};
